import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/axiosConfig'
import { getStorageUrl } from '../../services/apiService'

const MyEvents = () => {
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('upcoming')
  const [showModal, setShowModal] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [players, setPlayers] = useState([])
  const [selectedPlayersByCategory, setSelectedPlayersByCategory] = useState({})
  const [registrationNotes, setRegistrationNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [toast, setToast] = useState({ show: false, message: '', type: '' })
  const [genderPopup, setGenderPopup] = useState({ show: false, event: null })
  const [selectedYear, setSelectedYear] = useState(null)

  // Category mapping: Short code to Full name
  const categoryMapping = {
    'LL': 'Little League',
    'SJ': 'Sub Junior',
    'JL': 'Junior',
    'SL': 'Senior',
    'U-12': 'Little League',
    'U-15': 'Sub Junior',
    'U-18': 'Junior'
  }

  // Normalize category - convert short code to full name
  const normalizeCategory = (category) => {
    if (!category) return category
    const trimmed = category.trim()
    return categoryMapping[trimmed] || trimmed
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type })
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' })
    }, 4000)
  }

  useEffect(() => {
    fetchEvents()
    fetchPlayers()
  }, [])

  const fetchEvents = async () => {
    try {
      setLoading(true)
      const response = await api.get('/user/events')
      if (response.data.success) {
        // Store ALL events - past tab needs non-participated events too
        setEvents(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching events:', error)
      if (error.response?.status !== 401) {
        alert('Failed to fetch events')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchPlayers = async () => {
    try {
      const response = await api.get('/user/my-players', {
        params: { status: 'active' }
      })
      if (response.data.success) {
        setPlayers(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching players:', error)
    }
  }

  const handleParticipateClick = (event) => {
    if (!event.can_register) {
      showToast('Registration is not available for this event', 'error')
      return
    }
    // Allow adding more players even if some are already registered
    setSelectedEvent(event)
    setSelectedPlayersByCategory({})
    setRegistrationNotes('')
    setShowModal(true)
  }

  const handlePlayerSelect = (category, playerId) => {
    setSelectedPlayersByCategory(prev => {
      const categoryPlayers = prev[category] || []

      if (categoryPlayers.includes(playerId)) {
        // Remove player from this category
        const updated = categoryPlayers.filter(id => id !== playerId)
        if (updated.length === 0) {
          // Remove category if no players selected
          const { [category]: _, ...rest } = prev
          return rest
        }
        return { ...prev, [category]: updated }
      } else {
        // Add player to this category
        return { ...prev, [category]: [...categoryPlayers, playerId] }
      }
    })
  }

  // Get event categories as array
  const getEventCategories = (event) => {
    if (!event.event_level) return []
    return event.event_level.split(',').map(cat => normalizeCategory(cat))
  }

  // Check if player is already registered in a specific category
  const isPlayerRegisteredInCategory = (event, playerId, category) => {
    if (!event.registered_player_categories) return false
    return event.registered_player_categories.some(
      reg => reg.player_id === playerId && reg.category === category
    )
  }

  const handleSubmitParticipation = async (e) => {
    e.preventDefault()

    // Build registrations array from selectedPlayersByCategory
    const registrations = []
    Object.entries(selectedPlayersByCategory).forEach(([category, playerIds]) => {
      playerIds.forEach(playerId => {
        registrations.push({
          player_id: playerId,
          category: category
        })
      })
    })

    if (registrations.length === 0) {
      alert('Please select at least one player for at least one category')
      return
    }

    setSubmitting(true)

    try {
      const response = await api.post(`/user/events/${selectedEvent.id}/participate`, {
        registrations: registrations,
        registration_notes: registrationNotes
      })

      if (response.data.success) {
        showToast(response.data.message, 'success')
        setShowModal(false)
        fetchEvents() // Refresh events list
      }
    } catch (error) {
      console.error('Error registering for event:', error)
      showToast(error.response?.data?.message || 'Failed to register for event', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'published': 'status-published',
      'registration_open': 'status-open',
      'registration_closed': 'status-closed',
      'ongoing': 'status-ongoing',
      'completed': 'status-completed',
      'cancelled': 'status-cancelled'
    }
    return statusMap[status] || 'status-default'
  }

  const getRegistrationStatusBadge = (regStatus) => {
    const statusMap = {
      'open': { text: 'Registration Open', class: 'reg-open' },
      'closed': { text: 'Registration Closed', class: 'reg-closed' },
      'not_started': { text: 'Coming Soon', class: 'reg-coming' },
      'not_available': { text: 'N/A', class: 'reg-na' }
    }
    return statusMap[regStatus] || { text: regStatus, class: 'reg-default' }
  }

  const handleBackClick = () => {
    navigate('/user/dashboard')
  }

  // Get session year from a date (April to March cycle)
  // Jan-Mar 2026 = "2025-26", Apr-Dec 2025 = "2025-26"
  const getSessionYear = (date) => {
    const d = new Date(date)
    const month = d.getMonth() // 0-indexed (0=Jan, 3=Apr)
    const year = d.getFullYear()
    const startYear = month < 3 ? year - 1 : year // Jan-Mar belongs to previous session
    return `${startYear}-${String(startYear + 1).slice(2)}`
  }

  // Get available session years from past events
  const getAvailableYears = () => {
    const now = new Date()
    const pastEvents = events.filter(event => {
      const endDate = new Date(event.event_end_date)
      endDate.setHours(23, 59, 59, 999)
      return endDate < now || event.status === 'completed'
    })
    const sessionYears = [...new Set(pastEvents.map(event => getSessionYear(event.event_end_date)))].sort((a, b) => b.localeCompare(a))
    return sessionYears
  }

  // Filter events by past/upcoming
  // Tournament moves to past only after event_end_date day is fully over
  const getFilteredEvents = () => {
    const now = new Date()

    if (activeTab === 'past') {
      let result = events.filter(event => {
        if (event.status === 'completed') return true
        const endDate = new Date(event.event_end_date)
        endDate.setHours(23, 59, 59, 999)
        return now > endDate
      })
      // Apply session year filter
      if (selectedYear) {
        result = result.filter(event => getSessionYear(event.event_end_date) === selectedYear)
      }
      return result
    } else {
      // Upcoming tab: event end date hasn't fully passed yet
      return events.filter(event => {
        if (event.status === 'completed') return false
        const endDate = new Date(event.event_end_date)
        endDate.setHours(23, 59, 59, 999)
        return now <= endDate
      })
    }
  }

  const availableYears = getAvailableYears()

  // Get participation status badge config
  const getParticipationStatusBadge = (status) => {
    const statusMap = {
      'not_participated': { text: 'Not Participated', icon: 'fas fa-times-circle', bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
      'participated': { text: 'Participated', icon: 'fas fa-check-circle', bg: '#dcfce7', color: '#166534', border: '#86efac' },
      'winner': { text: 'Winner', icon: 'fas fa-trophy', bg: '#fef3c7', color: '#92400e', border: '#fcd34d' }
    }
    return statusMap[status] || statusMap['not_participated']
  }

  const filteredEvents = getFilteredEvents()

  return (
    <div className="my-events-page">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <i className={`fas fa-${toast.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          <span>{toast.message}</span>
        </div>
      )}

      <div className="page-header">
        <div className="header-left">
          <div>
            <h1>My Tournaments</h1>
            <p>Track your tournament participation and results</p>
          </div>
        </div>
        <button
          className="back-button"
          onClick={handleBackClick}
          type="button"
        >
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      {/* Past/Upcoming Tabs */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        display: 'flex',
        gap: '1rem'
      }}>
        <button
          onClick={() => setActiveTab('upcoming')}
          style={{
            flex: 1,
            padding: '1rem',
            border: activeTab === 'upcoming' ? '2px solid #c00f15' : '2px solid #e5e7eb',
            borderRadius: '10px',
            background: activeTab === 'upcoming' ? 'rgba(192, 15, 21, 0.1)' : 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            transition: 'all 0.2s',
            fontSize: '1rem',
            fontWeight: '600',
            color: activeTab === 'upcoming' ? '#c00f15' : '#6b7280'
          }}
        >
          <i className="fas fa-calendar-plus" style={{ fontSize: '1.5rem' }}></i>
          Upcoming Tournaments
        </button>
        <button
          onClick={() => setActiveTab('past')}
          style={{
            flex: 1,
            padding: '1rem',
            border: activeTab === 'past' ? '2px solid #c00f15' : '2px solid #e5e7eb',
            borderRadius: '10px',
            background: activeTab === 'past' ? 'rgba(192, 15, 21, 0.1)' : 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            transition: 'all 0.2s',
            fontSize: '1rem',
            fontWeight: '600',
            color: activeTab === 'past' ? '#c00f15' : '#6b7280'
          }}
        >
          <i className="fas fa-history" style={{ fontSize: '1.5rem' }}></i>
          Past Tournaments
        </button>
      </div>

      {/* Year filter for Past Tournaments */}
      {activeTab === 'past' && availableYears.length > 0 && (
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          background: '#fafafa',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          <span style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '500' }}>
            Filter by Year:
          </span>
          <select
            value={selectedYear || ''}
            onChange={(e) => setSelectedYear(e.target.value || null)}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              background: 'white',
              color: '#4b5563',
              fontWeight: '500',
              cursor: 'pointer',
              fontSize: '0.85rem',
              outline: 'none'
            }}
          >
            <option value="">All Years</option>
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading events...</p>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem 2rem',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.25rem'
          }}>
            <i className={activeTab === 'past' ? 'fas fa-history' : 'fas fa-calendar-alt'} style={{
              fontSize: '2rem',
              color: '#c00f15'
            }}></i>
          </div>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: '700',
            color: '#1f2937',
            margin: '0 0 0.5rem'
          }}>
            {activeTab === 'past' ? 'No Past Tournaments' : 'No Upcoming Tournaments'}
          </h3>
          <p style={{
            fontSize: '0.9rem',
            color: '#6b7280',
            margin: '0 0 1.5rem',
            maxWidth: '400px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            {activeTab === 'past'
              ? 'You have no past tournament records yet. Participate in upcoming tournaments to build your history.'
              : 'No upcoming tournaments available right now. Check back later for new events.'}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {activeTab === 'upcoming' && (
              <button
                onClick={() => navigate('/user/events')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 24px',
                  background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(192, 15, 21, 0.3)'
                }}
              >
                <i className="fas fa-calendar-alt"></i>
                View All Tournaments
              </button>
            )}
            <button
              onClick={() => setActiveTab(activeTab === 'past' ? 'upcoming' : 'past')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 24px',
                background: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              <i className={activeTab === 'past' ? 'fas fa-arrow-right' : 'fas fa-arrow-left'}></i>
              {activeTab === 'past' ? 'View Upcoming Tournaments' : 'View Past Tournaments'}
            </button>
          </div>
        </div>
      ) : (
        <div className="table-container">
          <table className="events-table">
            <thead>
              <tr>
                <th>Event Name</th>
                <th>Dates</th>
                <th>Venue</th>
                {activeTab === 'past' ? (
                  <>
                    <th>Status</th>
                    <th>Actions</th>
                  </>
                ) : (
                  <>
                    <th>Last Date to Participate</th>
                    <th>Actions</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredEvents.map(event => {
                const regStatus = getRegistrationStatusBadge(event.registration_status)
                const participationBadge = getParticipationStatusBadge(event.participation_status)
                return (
                  <tr key={event.id} className="event-row">
                    <td className="event-name-cell">
                      <div className="event-name-wrapper">
                        {event.event_image && (
                          <img
                            src={getStorageUrl(event.event_image)}
                            alt={event.event_name}
                            className="event-thumbnail"
                          />
                        )}
                        <div>
                          <div className="event-name">{event.event_name}</div>
                          {event.event_description && (
                            <div className="event-desc">{event.event_description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="dates-cell">{event.formatted_dates}</td>
                    <td className="venue-cell">{event.event_venue}{event.event_city ? `, ${event.event_city}` : ''}</td>

                    {activeTab === 'past' ? (
                      <>
                        {/* Past tab columns: Status, Actions */}
                        <td>
                          <span
                            className="participation-badge"
                            style={{
                              background: '#dcfce7',
                              color: '#166534',
                              border: '1px solid #86efac',
                              padding: '0.35rem 0.75rem',
                              borderRadius: '20px',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.35rem',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <i className="fas fa-check-circle" style={{ fontSize: '0.65rem' }}></i>
                            Participated
                          </span>
                        </td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                              <button
                                className="btn-get-details"
                                onClick={() => navigate(`/user/events/${event.id}/results`)}
                                title="View Tournament Results"
                                style={{
                                  padding: '0.5rem 0.8rem',
                                  background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontWeight: '600',
                                  fontSize: '0.7rem',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.35rem',
                                  whiteSpace: 'nowrap',
                                  boxShadow: '0 2px 8px rgba(192, 15, 21, 0.35)',
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                <i className="fas fa-info-circle"></i>
                                Get Details
                              </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      (() => {
                        // Use registration_end_date if available, otherwise fallback to 1 day before event start
                        let lastDate = null
                        if (event.registration_end_date) {
                          lastDate = new Date(event.registration_end_date + 'T00:00:00')
                        } else if (event.event_start_date) {
                          lastDate = new Date(event.event_start_date + 'T00:00:00')
                          lastDate.setDate(lastDate.getDate() - 1)
                        }
                        const now = new Date()

                        // Deadline is end of day on the last date
                        let isExpired = false
                        if (lastDate) {
                          const deadline = new Date(lastDate)
                          deadline.setHours(23, 59, 59, 999)
                          isExpired = now > deadline
                        }

                        return (
                          <>
                            {/* Last Date to Participate */}
                            <td>
                              {lastDate ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                                  <span style={{
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    background: isExpired ? '#fee2e2' : '#dcfce7',
                                    color: isExpired ? '#991b1b' : '#166534',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.3rem',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    <i className={isExpired ? 'fas fa-times-circle' : 'fas fa-calendar-check'} style={{ fontSize: '0.65rem' }}></i>
                                    {lastDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  </span>
                                  <span style={{
                                    fontSize: '0.6rem',
                                    color: isExpired ? '#991b1b' : '#6b7280',
                                    paddingLeft: '0.6rem'
                                  }}>
                                    {isExpired ? 'Deadline passed' : 'Till 12:00 PM'}
                                  </span>
                                </div>
                              ) : (
                                <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>N/A</span>
                              )}
                            </td>
                            {/* Actions */}
                            <td className="actions-cell">
                              <div className="action-buttons">
                                {isExpired || !event.can_register ? (
                                  <button
                                    className="btn-participate"
                                    disabled
                                    title={isExpired ? 'Registration deadline (12:00 PM) has passed' : 'Registration not available'}
                                    style={{
                                      padding: '0.5rem 0.8rem',
                                      background: '#e5e7eb',
                                      color: '#9ca3af',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'not-allowed',
                                      fontWeight: '600',
                                      fontSize: '0.7rem',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '0.35rem',
                                      opacity: 0.6
                                    }}
                                  >
                                    <i className="fas fa-user-plus"></i>
                                    Participate
                                  </button>
                                ) : (
                                  <button
                                    className="btn-participate"
                                    onClick={() => navigate(`/user/events/${event.id}/participate`)}
                                    title="Participate in Tournament"
                                  >
                                    <i className="fas fa-user-plus"></i>
                                    Participate
                                  </button>
                                )}
                              </div>
                            </td>
                          </>
                        )
                      })()
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Participate Modal */}
      {showModal && selectedEvent && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Participate in Event</h2>
              <button className="close-button" onClick={() => setShowModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="event-info">
                <h3>{selectedEvent.event_name}</h3>
                <p>{selectedEvent.formatted_dates}</p>
                <p>{selectedEvent.event_venue}</p>
              </div>

              <form onSubmit={handleSubmitParticipation}>
                <div className="event-category-info">
                  <p><strong>Event Categories:</strong> {selectedEvent.level_name}</p>
                  <p className="info-note">Select players for each category. A player can participate in multiple categories.</p>
                </div>

                {players.length === 0 ? (
                  <p className="no-players">You have no active players. Please add players first.</p>
                ) : (
                  <div className="categories-container">
                    {getEventCategories(selectedEvent).map(category => (
                      <div key={category} className="category-section">
                        <h4 className="category-title">
                          <i className="fas fa-tag"></i> {category}
                        </h4>
                        <div className="players-list">
                          {players.map(player => {
                            const isAlreadyRegistered = isPlayerRegisteredInCategory(selectedEvent, player.id, category)
                            const isSelected = selectedPlayersByCategory[category]?.includes(player.id)

                            return (
                              <label
                                key={player.id}
                                className={`player-checkbox ${isAlreadyRegistered ? 'already-registered' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected || false}
                                  onChange={() => handlePlayerSelect(category, player.id)}
                                  disabled={isAlreadyRegistered}
                                />
                                <div className="player-info">
                                  <span className="player-name">
                                    {player.full_name}
                                    {isAlreadyRegistered && (
                                      <span className="registered-indicator">
                                        <i className="fas fa-check-circle"></i> Already Registered
                                      </span>
                                    )}
                                  </span>
                                  <span className="player-details">
                                    {player.age} years • {player.gender} • {player.weight ? `${player.weight} kg` : 'Weight N/A'} • {player.player_reg_id}
                                  </span>
                                </div>
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="form-group">
                  <label>Registration Notes (Optional)</label>
                  <textarea
                    value={registrationNotes}
                    onChange={(e) => setRegistrationNotes(e.target.value)}
                    rows="3"
                    placeholder="Any additional information..."
                  ></textarea>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setShowModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={submitting || Object.keys(selectedPlayersByCategory).length === 0}
                  >
                    {submitting ? 'Registering...' : 'Register Players'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .my-events-page {
          padding: 2rem;
          width: 100%;
          background: #f9fafb;
          min-height: 100vh;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .header-left {
          display: flex;
          gap: 1.5rem;
          align-items: center;
        }

        .back-button {
          padding: 0.75rem 1.5rem;
          background: #c00f15;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.95rem;
          font-weight: 600;
          transition: all 0.3s;
          position: relative;
          z-index: 10;
          pointer-events: auto;
        }

        .back-button:hover {
          background: #a00d12;
        }

        .back-button:active {
          transform: scale(0.98);
        }

        .page-header h1 {
          color: #c00f15;
          margin-bottom: 0.25rem;
        }

        .page-header p {
          color: #6b7280;
        }

        .loading-state, .no-data {
          text-align: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .loading-state i, .no-data i {
          font-size: 3rem;
          margin-bottom: 1rem;
          color: #9ca3af;
        }

        .loading-state p, .no-data p {
          color: #6b7280;
          font-size: 1.1rem;
        }

        .table-container {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          overflow-x: auto;
          border: 1px solid #e5e7eb;
        }

        .events-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 0.85rem;
          table-layout: auto;
        }

        .events-table thead {
          background: linear-gradient(135deg, #c00f15 0%, #a00d12 100%) !important;
        }

        .events-table th {
          padding: 0.85rem 0.6rem;
          text-align: left;
          font-weight: 600;
          color: white !important;
          white-space: nowrap;
          text-transform: uppercase;
          font-size: 0.65rem;
          letter-spacing: 0.5px;
          background: linear-gradient(135deg, #c00f15 0%, #a00d12 100%) !important;
        }

        .events-table th:first-child {
          border-radius: 16px 0 0 0;
        }

        .events-table th:last-child {
          border-radius: 0 16px 0 0;
        }

        .events-table td {
          padding: 0.85rem 0.6rem;
          border-bottom: 1px solid #f3f4f6;
          color: #4b5563;
          vertical-align: middle;
          background: white;
          transition: all 0.2s ease;
        }

        .events-table tbody tr {
          transition: all 0.2s ease;
        }

        .events-table tbody tr:hover td {
          background: linear-gradient(135deg, #fef2f2 0%, #fff5f5 100%);
        }

        .events-table tbody tr:last-child td {
          border-bottom: none;
        }

        .events-table tbody tr:last-child td:first-child {
          border-radius: 0 0 0 16px;
        }

        .events-table tbody tr:last-child td:last-child {
          border-radius: 0 0 16px 0;
        }

        .event-name-cell {
          min-width: 250px;
          max-width: 280px;
        }

        .dates-cell {
          min-width: 90px;
          white-space: nowrap;
        }

        .venue-cell {
          min-width: 140px;
          max-width: 160px;
          line-height: 1.4;
        }

        .event-name-wrapper {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .event-thumbnail {
          width: 50px;
          height: 50px;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          border: 2px solid #f3f4f6;
        }

        .event-name {
          color: #c00f15;
          font-weight: 700;
          margin-bottom: 0.25rem;
          font-size: 0.85rem;
          transition: color 0.2s;
        }

        .events-table tbody tr:hover .event-name {
          color: #a00d12;
        }

        .event-desc {
          color: #6b7280;
          font-size: 0.7rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.4;
        }

        .status-badge {
          padding: 0.3rem 0.6rem;
          border-radius: 20px;
          font-size: 0.65rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          white-space: nowrap;
          border: 1px solid transparent;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
        }

        .status-published {
          background: linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%);
          color: #c00f15;
          border-color: #f48fb1;
        }

        .status-open {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          color: #166534;
          border-color: #86efac;
        }

        .status-closed {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #991b1b;
          border-color: #fca5a5;
        }

        .status-ongoing {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          color: #92400e;
          border-color: #fcd34d;
        }

        .status-completed {
          background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
          color: #374151;
          border-color: #9ca3af;
        }

        .participated-badge {
          padding: 0.4rem 0.75rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          color: #166534;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          white-space: nowrap;
          border: 1px solid #86efac;
          box-shadow: 0 1px 3px rgba(22, 101, 52, 0.1);
        }

        .participated-badge i {
          font-size: 0.9rem;
        }

        .registered-info {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          align-items: flex-start;
        }

        .member-breakdown {
          display: flex;
          gap: 0.25rem;
          flex-wrap: wrap;
        }

        .breakdown-item {
          padding: 0.2rem 0.45rem;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 0.2rem;
          white-space: nowrap;
        }

        .breakdown-player {
          background: #dbeafe;
          color: #1e40af;
        }

        .breakdown-coach {
          background: #dcfce7;
          color: #166534;
        }

        .breakdown-manager {
          background: #ede9fe;
          color: #6b21a8;
        }

        .breakdown-item i {
          font-size: 0.6rem;
        }

        .gender-count {
          display: flex;
          align-items: center;
          gap: 0.15rem;
        }

        .male-count {
          color: #1d4ed8;
          font-weight: 600;
        }

        .female-count {
          color: #db2777;
          font-weight: 600;
        }

        .breakdown-player .male-count,
        .breakdown-player .female-count {
          color: inherit;
        }

        .breakdown-coach .male-count,
        .breakdown-coach .female-count {
          color: inherit;
        }

        .breakdown-manager .male-count,
        .breakdown-manager .female-count {
          color: inherit;
        }

        .reg-status {
          font-weight: 500;
          font-size: 0.75rem;
        }

        .reg-open {
          color: #166534;
        }

        .reg-closed {
          color: #991b1b;
        }

        .reg-coming {
          color: #92400e;
        }

        .actions-cell {
          white-space: nowrap;
          min-width: 100px;
        }

        .action-buttons {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          align-items: flex-start;
        }

        .btn-view-result {
          padding: 0.5rem 0.8rem;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.7rem;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          transition: all 0.3s ease;
          white-space: nowrap;
          box-shadow: 0 2px 8px rgba(245, 158, 11, 0.35);
        }

        .btn-view-result:hover {
          background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.5);
          transform: translateY(-2px);
        }

        .btn-participate {
          padding: 0.5rem 0.8rem;
          background: linear-gradient(135deg, #c00f15 0%, #a00d12 100%);
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.7rem;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          transition: all 0.3s ease;
          white-space: nowrap;
          box-shadow: 0 2px 8px rgba(192, 15, 21, 0.35);
        }

        .btn-participate:hover {
          background: linear-gradient(135deg, #a00d12 0%, #8a0b10 100%);
          box-shadow: 0 4px 12px rgba(192, 15, 21, 0.5);
          transform: translateY(-2px);
        }

        .btn-participate:active,
        .btn-view-result:active {
          transform: translateY(0);
        }

        .cannot-register {
          color: #9ca3af;
          font-size: 0.85rem;
          font-style: italic;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 2rem;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          color: #111827;
          margin: 0;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 1.5rem;
          color: #6b7280;
          cursor: pointer;
          padding: 0.5rem;
          transition: all 0.3s;
        }

        .close-button:hover {
          color: #111827;
        }

        .modal-body {
          padding: 1.5rem;
        }

        .event-info {
          background: #f9fafb;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .event-info h3 {
          color: #111827;
          margin-bottom: 0.5rem;
        }

        .event-info p {
          color: #6b7280;
          font-size: 0.875rem;
          margin: 0.25rem 0;
        }

        .event-category-info {
          background: #fef2f2;
          border-left: 4px solid #c00f15;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .event-category-info p {
          margin: 0.25rem 0;
          color: #c00f15;
        }

        .event-category-info .info-note {
          font-size: 0.875rem;
          color: #6b7280;
          font-style: italic;
          margin-top: 0.5rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          color: #374151;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }

        .weight-filter-select {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 1rem;
          background: white;
          cursor: pointer;
          transition: all 0.3s;
        }

        .weight-filter-select:focus {
          outline: none;
          border-color: #c00f15;
          box-shadow: 0 0 0 3px rgba(192, 15, 21, 0.1);
        }

        .no-players {
          color: #ef4444;
          font-size: 0.875rem;
          padding: 1rem;
          background: #fef2f2;
          border-radius: 8px;
          text-align: center;
        }

        .categories-container {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          max-height: 500px;
          overflow-y: auto;
          padding: 0.5rem;
        }

        .category-section {
          background: #f9fafb;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 1rem;
        }

        .category-title {
          color: #c00f15;
          font-size: 1rem;
          font-weight: 600;
          margin: 0 0 1rem 0;
          padding: 0.5rem;
          background: white;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .category-title i {
          font-size: 0.9rem;
        }

        .players-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          max-height: 250px;
          overflow-y: auto;
          padding: 0.5rem;
        }

        .player-checkbox {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .player-checkbox:hover {
          border-color: #c00f15;
          background: #f9fafb;
        }

        .player-checkbox.already-registered {
          background: #eff6ff;
          border-color: #bfdbfe;
          cursor: not-allowed;
        }

        .player-checkbox.already-registered:hover {
          border-color: #bfdbfe;
          background: #eff6ff;
        }

        .player-checkbox input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .player-checkbox input[type="checkbox"]:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .player-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .player-name {
          color: #111827;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .registered-indicator {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          color: #2563eb;
          font-size: 0.75rem;
          font-weight: 500;
          padding: 0.125rem 0.5rem;
          background: #dbeafe;
          border-radius: 12px;
        }

        .registered-indicator i {
          font-size: 0.875rem;
        }

        .player-details {
          color: #6b7280;
          font-size: 0.875rem;
        }

        .form-group textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 1rem;
          resize: vertical;
          font-family: inherit;
        }

        .form-group textarea:focus {
          outline: none;
          border-color: #c00f15;
          box-shadow: 0 0 0 3px rgba(192, 15, 21, 0.1);
        }

        .modal-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          padding-top: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .btn-cancel, .btn-submit {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s;
        }

        .btn-cancel {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-cancel:hover {
          background: #e5e7eb;
        }

        .btn-submit {
          background: #10b981;
          color: white;
        }

        .btn-submit:hover {
          background: #059669;
        }

        .btn-submit:disabled, .btn-cancel:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Toast Notification */
        .toast {
          position: fixed;
          top: 2rem;
          right: 2rem;
          padding: 1rem 1.5rem;
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: center;
          gap: 0.75rem;
          z-index: 10000;
          animation: slideIn 0.3s ease-out;
          min-width: 300px;
          max-width: 500px;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .toast-success {
          border-left: 4px solid #10b981;
        }

        .toast-success i {
          color: #10b981;
          font-size: 1.25rem;
        }

        .toast-error {
          border-left: 4px solid #ef4444;
        }

        .toast-error i {
          color: #ef4444;
          font-size: 1.25rem;
        }

        .toast span {
          color: #374151;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        @media (max-width: 768px) {
          .my-events-page {
            padding: 1rem;
          }

          .page-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
            padding: 1rem;
          }

          .header-left {
            flex-direction: column;
            align-items: flex-start;
          }

          .table-container {
            overflow-x: auto;
            border-radius: 12px;
          }

          .events-table {
            font-size: 0.8rem;
          }

          .events-table th {
            padding: 1rem 0.75rem;
            font-size: 0.7rem;
          }

          .events-table td {
            padding: 1rem 0.75rem;
          }

          .events-table th:first-child {
            border-radius: 12px 0 0 0;
          }

          .events-table th:last-child {
            border-radius: 0 12px 0 0;
          }

          .event-thumbnail {
            width: 45px;
            height: 45px;
          }

          .event-name-cell {
            min-width: 200px;
          }

          .btn-participate,
          .btn-view-result {
            padding: 0.5rem 0.8rem;
            font-size: 0.75rem;
          }

          .modal-overlay {
            padding: 1rem;
          }

          .toast {
            top: 1rem;
            right: 1rem;
            left: 1rem;
            min-width: auto;
          }
        }

        /* Gender Popup Styles */
        .gender-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .gender-popup {
          background: white;
          border-radius: 12px;
          padding: 0;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: popupSlideIn 0.2s ease-out;
        }

        @keyframes popupSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .gender-popup-header {
          padding: 1rem 1.25rem;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: linear-gradient(135deg, #c00f15 0%, #a00d12 100%);
          border-radius: 12px 12px 0 0;
        }

        .gender-popup-title-section {
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }

        .gender-popup-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: rgb(169 9 9 / 85%);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .gender-popup-event-name {
          font-size: 0.75rem;
          color: rgb(169 9 9 / 85%);
          font-weight: 400;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 280px;
        }

        .gender-popup-close {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2rem;
          transition: background 0.2s;
        }

        .gender-popup-close:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .gender-popup-body {
          padding: 1.25rem;
          max-height: 60vh;
          overflow-y: auto;
        }

        .category-section {
          margin-bottom: 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          overflow: hidden;
        }

        .category-section:last-of-type {
          margin-bottom: 1rem;
        }

        .category-header {
          padding: 0.75rem 1rem;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          font-size: 0.9rem;
          color: #374151;
        }

        .category-header i {
          color: #c00f15;
        }

        .category-total {
          margin-left: auto;
          background: #c00f15;
          color: white;
          padding: 0.2rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .category-body {
          padding: 0.75rem;
          background: white;
        }

        .member-type-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem 0.5rem;
          border-radius: 6px;
          margin-bottom: 0.35rem;
          background: #f9fafb;
        }

        .member-type-row:last-child {
          margin-bottom: 0;
        }

        .member-type-label {
          font-size: 0.8rem;
          color: #4b5563;
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }

        .member-type-label i {
          font-size: 0.7rem;
          color: #9ca3af;
        }

        .gender-badges {
          display: flex;
          gap: 0.5rem;
        }

        .gender-badge {
          padding: 0.25rem 0.6rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .gender-badge.male {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .gender-badge.female {
          background: #fce7f3;
          color: #db2777;
        }

        .no-breakdown {
          text-align: center;
          padding: 2rem;
          color: #6b7280;
        }

        .gender-section {
          margin-bottom: 1rem;
        }

        .gender-section:last-child {
          margin-bottom: 0;
        }

        .gender-section-title {
          font-size: 0.8rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.5rem;
        }

        .gender-row {
          display: flex;
          gap: 0.75rem;
        }

        .gender-box {
          flex: 1;
          padding: 0.75rem;
          border-radius: 8px;
          text-align: center;
        }

        .gender-box.male {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
        }

        .gender-box.female {
          background: #fdf2f8;
          border: 1px solid #fbcfe8;
        }

        .gender-box .count {
          font-size: 1.5rem;
          font-weight: 700;
        }

        .gender-box.male .count {
          color: #1d4ed8;
        }

        .gender-box.female .count {
          color: #db2777;
        }

        .gender-box .label {
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 0.25rem;
        }

        .gender-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          background: #f9fafb;
          border-radius: 8px;
          margin-top: 1rem;
          border: 1px solid #e5e7eb;
        }

        .gender-total-label {
          font-size: 0.85rem;
          font-weight: 600;
          color: #374151;
        }

        .gender-total-count {
          font-size: 1.25rem;
          font-weight: 700;
          color: #c00f15;
        }

        .member-breakdown.clickable {
          cursor: pointer;
          transition: transform 0.15s, box-shadow 0.15s;
        }

        .member-breakdown.clickable:hover {
          transform: scale(1.05);
        }

        .member-breakdown.clickable:hover .breakdown-item {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }
      `}</style>

      {/* Gender Breakdown Popup */}
      {genderPopup.show && genderPopup.event && (
        <div className="gender-popup-overlay" onClick={() => setGenderPopup({ show: false, event: null })}>
          <div className="gender-popup" onClick={(e) => e.stopPropagation()}>
            <div className="gender-popup-header">
              <div className="gender-popup-title-section">
                <h3><i className="fas fa-users"></i> Participants</h3>
                <span className="gender-popup-event-name">{genderPopup.event.event_name}</span>
              </div>
              <button className="gender-popup-close" onClick={() => setGenderPopup({ show: false, event: null })}>
                &times;
              </button>
            </div>
            <div className="gender-popup-body">
              {/* Category-wise breakdown */}
              {genderPopup.event.category_breakdown && genderPopup.event.category_breakdown.length > 0 ? (
                <>
                  {genderPopup.event.category_breakdown.map((cat, idx) => (
                    <div key={idx} className="category-section">
                      <div className="category-header">
                        <i className="fas fa-trophy"></i>
                        <span>{cat.category || 'Uncategorized'}</span>
                        <span className="category-total">{cat.total}</span>
                      </div>
                      <div className="category-body">
                        {/* Players in this category */}
                        {cat.players.total > 0 && (
                          <div className="member-type-row">
                            <span className="member-type-label"><i className="fas fa-user"></i> Players</span>
                            <div className="gender-badges">
                              <span className="gender-badge male">{cat.players.male}M</span>
                              <span className="gender-badge female">{cat.players.female}F</span>
                            </div>
                          </div>
                        )}
                        {/* Coaches in this category */}
                        {cat.coaches.total > 0 && (
                          <div className="member-type-row">
                            <span className="member-type-label"><i className="fas fa-chalkboard-teacher"></i> Coaches</span>
                            <div className="gender-badges">
                              <span className="gender-badge male">{cat.coaches.male}M</span>
                              <span className="gender-badge female">{cat.coaches.female}F</span>
                            </div>
                          </div>
                        )}
                        {/* Managers in this category */}
                        {cat.managers.total > 0 && (
                          <div className="member-type-row">
                            <span className="member-type-label"><i className="fas fa-user-tie"></i> Managers</span>
                            <div className="gender-badges">
                              <span className="gender-badge male">{cat.managers.male}M</span>
                              <span className="gender-badge female">{cat.managers.female}F</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="no-breakdown">
                  <p>No category breakdown available</p>
                </div>
              )}

              {/* Total */}
              <div className="gender-total">
                <span className="gender-total-label">Total Registered</span>
                <span className="gender-total-count">{genderPopup.event.registered_players_count}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MyEvents
